/**
 * Where AuthKit lives, and who we are to it.
 *
 * The deployed value arrives through `window._env_` — Helm injects
 * `REACT_APP_WORKOS_CLIENT_ID` into the container and `start-*-app.sh`
 * writes it out at start-up, so staging and main can point at different
 * WorkOS environments without a rebuild.
 *
 * There is deliberately **no fallback literal** here. A client ID that
 * disagrees with the backend's fails as "invalid credentials", which
 * reads like a broken account rather than the deployment mistake it is —
 * so an empty value stops the flow at the button with something an
 * operator can act on instead.
 */
export function workosClientId(): string {
  const configured = window._env_?.REACT_APP_WORKOS_CLIENT_ID;
  return typeof configured === 'string' ? configured.trim() : '';
}

/** Whether the WorkOS sign-in button should be offered at all. */
export function isWorkosConfigured(): boolean {
  return workosClientId() !== '';
}

/**
 * Where AuthKit sends the browser back to. Must match a redirect URI
 * allow-listed in the WorkOS dashboard exactly, including the port —
 * WorkOS refuses anything it was not told about in advance.
 */
export function workosRedirectUri(): string {
  return `${window.location.origin}/callback`;
}

/** The key the CSRF `state` value is parked under between hops. */
export const WORKOS_STATE_KEY = 'workosState';

/**
 * Build the AuthKit authorization URL and the `state` that goes with it.
 *
 * `state` is random per attempt and checked when the browser comes back.
 * Without it, an attacker can feed a victim a code from *their own*
 * AuthKit session and silently sign the victim into the attacker's
 * account — login CSRF. The code exchange being server-side does not
 * prevent that; only matching the round trip does.
 */
export function buildAuthorizationUrl(): { url: string; state: string } {
  const state = crypto.randomUUID();

  const params = new URLSearchParams({
    client_id: workosClientId(),
    redirect_uri: workosRedirectUri(),
    response_type: 'code',
    provider: 'authkit',
    state,
  });

  return {
    url: `https://api.workos.com/user_management/authorize?${params}`,
    state,
  };
}
